import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext.js';

function BulkImportPage() {
    const { token } = useAuth();

    // State for adding new members
    const [addFile, setAddFile] = useState(null);
    const [isValidatingAdd, setIsValidatingAdd] = useState(false);
    const [isImportingAdd, setIsImportingAdd] = useState(false);
    const [addResult, setAddResult] = useState(null);
    const [addValidationComplete, setAddValidationComplete] = useState(false);
    const addFileInputRef = useRef(null);

    // State for updating members
    const [updateFile, setUpdateFile] = useState(null);
    const [isValidatingUpdate, setIsValidatingUpdate] = useState(false);
    const [isImportingUpdate, setIsImportingUpdate] = useState(false);
    const [updateResult, setUpdateResult] = useState(null);
    const [updateValidationComplete, setUpdateValidationComplete] = useState(false);
    const updateFileInputRef = useRef(null);

    // State for exporting
    const [isExporting, setIsExporting] = useState(false);
    const [exportError, setExportError] = useState(null);

    // --- Handlers for ADDING new members ---
    const handleAddFileChange = (event) => {
        setAddFile(event.target.files[0]);
        setAddResult(null);
        setAddValidationComplete(false);
    };

    const handleAddValidate = async () => {
        if (!addFile) return alert("Please select a file to upload.");
        setIsValidatingAdd(true);
        setAddResult(null);
        setAddValidationComplete(false);
        const formData = new FormData();
        formData.append('csvFile', addFile);
        formData.append('mode', 'validate');
        try {
            const response = await fetch('https://healthcard.searchisend.com/health_api/bulk_import_users.php', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.message || 'Validation failed.');
            setAddResult(result);
            if (result.successCount > 0) {
                setAddValidationComplete(true);
            }
        } catch (error) {
            setAddResult({ error: error.message });
        } finally {
            setIsValidatingAdd(false);
        }
    };

    const handleAddImport = async () => {
        if (!addFile) return alert("File not found. Please re-select.");
        setIsImportingAdd(true);
        const formData = new FormData();
        formData.append('csvFile', addFile);
        formData.append('mode', 'execute');
        try {
            const response = await fetch('https://healthcard.searchisend.com/health_api/bulk_import_users.php', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.message || 'Import failed.');
            setAddResult(result);
        } catch (error) {
            setAddResult({ error: error.message });
        } finally {
            setIsImportingAdd(false);
            setAddValidationComplete(false);
            if (addFileInputRef.current) addFileInputRef.current.value = "";
            setAddFile(null);
        }
    };

    // --- Handlers for UPDATING members ---
    const handleUpdateFileChange = (event) => {
        setUpdateFile(event.target.files[0]);
        setUpdateResult(null);
        setUpdateValidationComplete(false);
    };

    const handleUpdateValidate = async () => {
        if (!updateFile) return alert("Please select a file to upload.");
        setIsValidatingUpdate(true);
        setUpdateResult(null);
        setUpdateValidationComplete(false);
        const formData = new FormData();
        formData.append('csvFile', updateFile);
        formData.append('mode', 'validate');
        try {
            const response = await fetch('https://healthcard.searchisend.com/health_api/bulk_update_import.php', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.message || 'Validation failed.');
            setUpdateResult(result);
            if (result.successCount > 0) {
                setUpdateValidationComplete(true);
            }
        } catch (error) {
            setUpdateResult({ error: error.message });
        } finally {
            setIsValidatingUpdate(false);
        }
    };

    const handleUpdateImport = async () => {
        if (!updateFile) return alert("File not found. Please re-select.");
        setIsImportingUpdate(true);
        const formData = new FormData();
        formData.append('csvFile', updateFile);
        formData.append('mode', 'execute');
        try {
            const response = await fetch('https://healthcard.searchisend.com/health_api/bulk_update_import.php', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.message || 'Import failed.');
            setUpdateResult(result);
        } catch (error) {
            setUpdateResult({ error: error.message });
        } finally {
            setIsImportingUpdate(false);
            setUpdateValidationComplete(false);
            if (updateFileInputRef.current) updateFileInputRef.current.value = "";
            setUpdateFile(null);
        }
    };

    // --- Handler for EXPORT ---
    const handleExport = async () => {
        setIsExporting(true);
        setExportError(null);
        try {
            const response = await fetch('https://healthcard.searchisend.com/health_api/export_members_admin.php', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Export failed.');
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            const date = new Date().toISOString().slice(0, 10);
            a.download = `healthcard_full_export_${date}.csv`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            setExportError(error.message);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className="space-y-12">
            {/* Add New Members Section */}
            <div>
                <h2 className="text-2xl font-semibold text-gray-700">Add New Members via CSV</h2>
                <div className="mt-4 bg-gray-50 p-6 rounded-lg border border-dashed border-gray-300">
                    <div className="text-sm text-gray-600 mb-4">
                        <p className="font-semibold">Instructions:</p>
                        <ul className="list-disc list-inside mt-1">
                            <li>Use this for adding NEW members who are not in the system yet.</li>
                            <li>Required columns: <code className="bg-gray-200 p-1 rounded text-xs">firstName, phoneNumber</code>.</li>
                            <li>CSV headers: <code className="bg-gray-200 p-1 rounded text-xs">firstName,lastName,email,phoneNumber,state,city,profession,joiningDate</code></li>
                            <li>Date format: <strong className="text-brand-primary">DD-MM-YYYY</strong>.</li>
                        </ul>
                    </div>
                    <div className="flex items-center gap-4">
                        <input type="file" accept=".csv" onChange={handleAddFileChange} ref={addFileInputRef} className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100" />
                        <button onClick={handleAddValidate} disabled={isValidatingAdd || isImportingAdd || !addFile} className="bg-brand-primary hover:bg-red-900 text-white font-bold py-2 px-6 rounded-md disabled:bg-gray-400">{isValidatingAdd ? 'Validating...' : 'Validate File'}</button>
                    </div>
                    {addResult && (
                        <div className="mt-6 p-4 rounded-md bg-blue-50 text-blue-800 border border-blue-200">
                            <p className="font-bold">{addResult.message}</p>
                            {addResult.successCount !== undefined && <p>Valid new members found: {addResult.successCount}</p>}
                            {addResult.errorCount !== undefined && <p>Failed/Duplicate records: {addResult.errorCount}</p>}
                            {addResult.errors && addResult.errors.length > 0 && (
                                <div className="mt-2">
                                    <p className="font-semibold">Error Details:</p>
                                    <ul className="text-sm list-disc list-inside mt-1 max-h-40 overflow-y-auto">
                                        {addResult.errors.map((err, index) => <li key={index}>{err}</li>)}
                                    </ul>
                                </div>
                            )}
                            {addResult.error && <p className="text-red-500 font-semibold">{addResult.error}</p>}

                            {addValidationComplete && addResult.successCount > 0 && !addResult.error && (
                                <button onClick={handleAddImport} disabled={isImportingAdd} className="mt-4 bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-md disabled:bg-gray-400">
                                    {isImportingAdd ? 'Importing...' : `Confirm & Import ${addResult.successCount} New Members`}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Update/Restore Section */}
            <div>
                <h2 className="text-2xl font-semibold text-gray-700">Update/Restore Members via CSV</h2>
                <div className="mt-4 bg-yellow-50 p-6 rounded-lg border border-dashed border-yellow-300">
                    <div className="text-sm text-yellow-800 mb-4">
                        <p className="font-semibold">Instructions:</p>
                        <ul className="list-disc list-inside mt-1">
                            <li>Use this to update existing members from a previously exported file.</li>
                            <li>The CSV file MUST contain an <code className="bg-yellow-200 p-1 rounded text-xs">id</code> column from a full export.</li>
                            <li>This will overwrite existing data. Passwords will not be changed.</li>
                        </ul>
                    </div>
                    <div className="flex items-center gap-4">
                        <input type="file" accept=".csv" onChange={handleUpdateFileChange} ref={updateFileInputRef} className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-yellow-50 file:text-yellow-700 hover:file:bg-yellow-100" />
                        <button onClick={handleUpdateValidate} disabled={isValidatingUpdate || isImportingUpdate || !updateFile} className="bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-2 px-6 rounded-md disabled:bg-gray-400">{isValidatingUpdate ? 'Validating...' : 'Validate File'}</button>
                    </div>
                    {updateResult && (
                        <div className="mt-6 p-4 rounded-md bg-green-50 text-green-800 border border-green-200">
                            <p className="font-bold">{updateResult.message}</p>
                            {updateResult.successCount !== undefined && <p>Members to be updated: {updateResult.successCount}</p>}
                            {updateResult.errorCount !== undefined && <p>Failed records: {updateResult.errorCount}</p>}
                            {updateResult.errors && updateResult.errors.length > 0 && (
                                <div className="mt-2">
                                    <p className="font-semibold">Error Details:</p>
                                    <ul className="text-sm list-disc list-inside mt-1 max-h-40 overflow-y-auto">
                                        {updateResult.errors.map((err, index) => <li key={index}>{err}</li>)}
                                    </ul>
                                </div>
                            )}
                            {updateResult.error && <p className="text-red-500 font-semibold">{updateResult.error}</p>}

                            {updateValidationComplete && updateResult.successCount > 0 && !updateResult.error && (
                                <button onClick={handleUpdateImport} disabled={isImportingUpdate} className="mt-4 bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-md disabled:bg-gray-400">
                                    {isImportingUpdate ? 'Updating...' : `Confirm & Update ${updateResult.successCount} Members`}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Bulk Export Section */}
            <div>
                <h2 className="text-2xl font-semibold text-gray-700">Bulk Export Members</h2>
                <div className="mt-4 bg-gray-50 p-6 rounded-lg border border-dashed border-gray-300">
                    <p className="text-sm text-gray-600 mb-4">
                        Export all member data into a single CSV file. This is useful for backups and for use with the "Update/Restore" feature.
                    </p>
                    <button onClick={handleExport} disabled={isExporting} className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-md disabled:bg-gray-400">
                        {isExporting ? 'Exporting...' : 'Export Full Member List'}
                    </button>
                    {exportError && (
                        <p className="text-red-500 font-semibold mt-4">Error: {exportError}</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BulkImportPage;